import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { ChecklistRequirement, ComplianceChecklist, GetChecklistsRequest, RequirementEditor } from '../../../../../models/compliances.model';
import { CompliancesLibraryService } from '../../../../../services/compliances-library.service';
import { ComplianceOverviewService } from '../../../../../../compliance-overview/services/compliance-overview.service';
import { VehicleDocumentsService } from '../../../../../../../asset-overview/services/vehicle-documents.service';
import { VehicleDocumentType } from '../../../../../../../asset-overview/models/vehicle-documents.model';
import { ErrorHandlerService } from '../../../../../../../../_shared/services/error-handler.service';
import { ConfirmationModalService } from '../../../../../../../../_shared/services/modals/confirmation-modal.service';
import { EmptyStateComponent } from '../../../../../../../../_shared/components/states/empty-state/empty-state.component';
import { ErrorStateComponent } from '../../../../../../../../_shared/components/states/error-state/error-state.component';
import { ChecklistTableComponent } from '../../../../../../_shared/components/checklist-table/checklist-table.component';
import {
  ChecklistTableColumn,
  ChecklistTableGroup,
  ChecklistTableSectionConfig
} from '../../../../../../_shared/components/checklist-table/checklist-table.model';
import { CHECKLIST_TYPE_NAME_KEYS, CHECKLIST_TYPES, ChecklistTypeIdEnum } from '../../../../../../_shared/models/checklist-type.model';
import { ToastrService } from 'ngx-toastr';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../../../../../_shared/pipes/lookup-label.pipe';
import { translateLookupLabel } from '../../../../../../../../_shared/helpers/lookup-label.helper';

@Component({
  selector: 'app-checklist-requirements',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbDropdownModule,
    EmptyStateComponent,
    ErrorStateComponent,
    ChecklistTableComponent,
    TranslocoPipe,
    LookupLabelPipe
  ],
  templateUrl: './checklist-requirements.component.html',
  styleUrl: './checklist-requirements.component.scss'
})
export class ChecklistRequirementsComponent implements OnInit, OnDestroy {

  @Input() complianceId: number = 0;
  @Input() isArchive: boolean = false;

  private complianceService = inject(CompliancesLibraryService);
  private complianceOverviewService = inject(ComplianceOverviewService);
  private vehicleDocumentsService = inject(VehicleDocumentsService);
  private errorService = inject(ErrorHandlerService);
  private confirmationModalService = inject(ConfirmationModalService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancellation = new Subject<void>();

  /** Vehicle document type names are catalogue data; translated for display only. */
  protected readonly documentTypeLabelPrefix = 'assetOverview.documentTypeNames';

  checklistGroups: ChecklistTableGroup<ChecklistRequirement>[] = [];
  isLoading: boolean = true;
  hasError: boolean = false;

  documentTypes: VehicleDocumentType[] = [];
  private linkedTypeByRequirement = new Map<number, number>();
  private requirementByLinkedType = new Map<number, string>();

  addingGroupId: string | number | null = null;
  editingRequirementId: number | null = null;
  editor: RequirementEditor = { name: '', documentTypeId: null, critical: false };
  isSavingEditor = false;
  private editorChecklistTypeId: number | null = null;

  get checklistSections(): ChecklistTableSectionConfig[] {
    return this.getChecklistSections();
  }

  get isEmpty(): boolean {
    return this.checklistGroups.length === 0 || this.checklistGroups.every(group => group.rows.length === 0);
  }

  get editorTypeLabel(): string {
    const placeholder = this.transloco.translate('compliance.library.checklistRequirements.linkADocumentType');
    if (this.editor.documentTypeId === null) {
      return placeholder;
    }
    const name = this.documentTypes.find(type => type.id === this.editor.documentTypeId)?.name;
    return name ? translateLookupLabel(this.transloco, this.documentTypeLabelPrefix, name) : placeholder;
  }

  get isEditorSaveDisabled(): boolean {
    return this.isSavingEditor || !this.editor.name.trim();
  }

  /** Inspection requirements cannot link document types, so the editor hides the type dropdown. */
  get showEditorTypeDropdown(): boolean {
    return this.editorChecklistTypeId !== ChecklistTypeIdEnum.Inspection;
  }

  ngOnInit(): void {
    void this.loadAsync();
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  public refreshChecklistsAsync(): void {
    void this.loadAsync();
  }

  getLinkedTypeName(requirement: ChecklistRequirement): string | null {
    const typeId = this.linkedTypeByRequirement.get(requirement.id);
    if (typeId === undefined) {
      return null;
    }
    return this.documentTypes.find(type => type.id === typeId)?.name ?? null;
  }

  getTypeUsedBy(type: VehicleDocumentType): string | null {
    const usedBy = this.requirementByLinkedType.get(type.id);
    if (!usedBy) {
      return null;
    }

    if (this.editingRequirementId !== null) {
      const ownTypeId = this.linkedTypeByRequirement.get(this.editingRequirementId);
      if (ownTypeId === type.id) {
        return null;
      }
    }

    return usedBy;
  }

  startAdd(group: ChecklistTableGroup<ChecklistRequirement>): void {
    this.editingRequirementId = null;
    this.addingGroupId = group.id;
    this.editorChecklistTypeId = Number(group.type);
    this.editor = { name: '', documentTypeId: null, critical: false };
  }

  startEdit(requirement: ChecklistRequirement): void {
    this.addingGroupId = null;
    this.editingRequirementId = requirement.id;
    const group = this.findGroupForRequirement(requirement.id);
    this.editorChecklistTypeId = group ? Number(group.type) : null;
    this.editor = {
      name: requirement.name,
      documentTypeId: this.showEditorTypeDropdown ? this.linkedTypeByRequirement.get(requirement.id) ?? null : null,
      critical: requirement.isCritical
    };
  }

  cancelEditor(): void {
    this.addingGroupId = null;
    this.editingRequirementId = null;
  }

  selectEditorType(typeId: number | null): void {
    this.editor.documentTypeId = typeId;
  }

  async saveEditorAsync(): Promise<void> {
    if (this.editingRequirementId !== null) {
      const requirement = this.findRequirement(this.editingRequirementId);
      if (requirement) {
        await this.saveEditAsync(requirement);
      }
      return;
    }

    const group = this.checklistGroups.find(g => g.id === this.addingGroupId);
    if (group) {
      await this.saveAddAsync(group);
    }
  }

  private findRequirement(requirementId: number): ChecklistRequirement | null {
    return this.findGroupForRequirement(requirementId)?.rows.find(row => row.id === requirementId) ?? null;
  }

  private findGroupForRequirement(requirementId: number): ChecklistTableGroup<ChecklistRequirement> | null {
    return this.checklistGroups.find(group => group.rows.some(row => row.id === requirementId)) ?? null;
  }

  async saveAddAsync(group: ChecklistTableGroup<ChecklistRequirement>): Promise<void> {
    if (this.isEditorSaveDisabled) return;
    this.isSavingEditor = true;
    try {
      await this.complianceService.addComplianceChecklistRequirementsAsync({
        complianceId: this.complianceId,
        isNewCategory: false,
        checklistTypeId: Number(group.type),
        existingChecklistId: Number(group.id),
        newCategoryName: null,
        requirements: [{ name: this.editor.name.trim(), documentTypeId: this.editor.documentTypeId, isCritical: this.editor.critical }],
        allowDocumentUpload: true
      });
      this.toastr.success(this.transloco.translate('compliance.library.checklistRequirements.toasts.added'));
      this.cancelEditor();
      await this.loadAsync();
    } catch (error) {
      this.errorService.handleError(error, { showToast: true });
    } finally {
      this.isSavingEditor = false;
    }
  }

  async saveEditAsync(requirement: ChecklistRequirement): Promise<void> {
    if (this.isEditorSaveDisabled) return;
    this.isSavingEditor = true;
    try {
      await this.complianceService.updateChecklistRequirementDetailsAsync({
        requirementId: requirement.id,
        complianceId: this.complianceId,
        name: this.editor.name.trim(),
        documentTypeId: this.editor.documentTypeId,
        isCritical: this.editor.critical
      });
      this.toastr.success(this.transloco.translate('compliance.library.checklistRequirements.toasts.updated'));
      this.cancelEditor();
      await this.loadAsync();
    } catch (error) {
      this.errorService.handleError(error, { showToast: true });
    } finally {
      this.isSavingEditor = false;
    }
  }

  confirmUnlinkDocumentType(requirement: ChecklistRequirement): void {
    void this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('compliance.library.checklistRequirements.confirmations.unlink.title'),
      message: this.transloco.translate('compliance.library.checklistRequirements.confirmations.unlink.message'),
      warningMessage: this.transloco.translate('compliance.library.checklistRequirements.confirmations.unlink.warning'),
      confirmText: this.transloco.translate('compliance.library.checklistRequirements.confirmations.unlink.confirm'),
      declineText: this.transloco.translate('compliance.library.checklistRequirements.confirmations.unlink.decline'),
      onConfirm: async () => {
        await this.complianceService.updateChecklistRequirementDetailsAsync({
          requirementId: requirement.id,
          complianceId: this.complianceId,
          name: requirement.name,
          documentTypeId: null,
          isCritical: requirement.isCritical
        });
        await this.loadAsync();
      }
    });
  }

  async confirmDeleteRequirementAsync(requirement: ChecklistRequirement): Promise<void> {
    await this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('compliance.library.checklistRequirements.confirmations.delete.title'),
      message: this.transloco.translate('compliance.library.checklistRequirements.confirmations.delete.message', { name: requirement.name }),
      warningMessage: this.transloco.translate('compliance.library.checklistRequirements.confirmations.delete.warning'),
      confirmText: this.transloco.translate('compliance.library.checklistRequirements.confirmations.delete.confirm'),
      declineText: this.transloco.translate('compliance.library.checklistRequirements.confirmations.delete.decline'),
      onConfirm: async () => {
        await this.complianceService.deleteChecklistRequirementAsync(requirement.id, this.complianceId);
        await this.loadAsync();
      }
    });
  }

  async onArchiveRequirementAsync(requirement: ChecklistRequirement): Promise<void> {
    await this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('compliance.library.checklistRequirements.confirmations.archive.title'),
      message: this.transloco.translate('compliance.library.checklistRequirements.confirmations.archive.message'),
      warningMessage: this.transloco.translate('compliance.library.checklistRequirements.confirmations.archive.warning'),
      confirmText: this.transloco.translate('compliance.library.checklistRequirements.confirmations.archive.confirm'),
      declineText: this.transloco.translate('compliance.library.checklistRequirements.confirmations.archive.decline'),
      onConfirm: async () => {
        await this.complianceService.archiveChecklistRequirementAsync(requirement.id, this.complianceId);
        await this.loadAsync();
      }
    });
  }

  async onRestoreRequirementAsync(requirement: ChecklistRequirement): Promise<void> {
    await this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('compliance.library.checklistRequirements.confirmations.restore.title'),
      message: this.transloco.translate('compliance.library.checklistRequirements.confirmations.restore.message'),
      confirmText: this.transloco.translate('compliance.library.checklistRequirements.confirmations.restore.confirm'),
      declineText: this.transloco.translate('compliance.library.checklistRequirements.confirmations.restore.decline'),
      onConfirm: async () => {
        await this.complianceService.restoreChecklistRequirementAsync(requirement.id, this.complianceId);
        await this.loadAsync();
      }
    });
  }

  private async loadAsync(): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;
      const request: GetChecklistsRequest = {
        complianceId: this.complianceId,
        isArchived: this.isArchive
      };

      const [checklistsResponse] = await Promise.all([
        this.complianceService.getChecklistsAsync(request, this.cancellation),
        this.loadLinksAsync(),
        this.loadDocumentTypesAsync()
      ]);
      const checklists = checklistsResponse.data ?? [];

      this.checklistGroups = this.mapChecklistGroups(checklists);

      this.isLoading = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private async loadLinksAsync(): Promise<void> {
    try {
      const response = await this.complianceOverviewService.getRequirementDocumentLinksAsync(this.complianceId);
      const links = response.data ?? [];
      this.linkedTypeByRequirement = new Map(links.map(link => [link.requirementId, link.documentTypeId]));
      this.requirementByLinkedType = new Map(links.map(link => [link.documentTypeId, link.requirementName]));
    } catch {
      this.linkedTypeByRequirement = new Map();
      this.requirementByLinkedType = new Map();
    }
  }

  private async loadDocumentTypesAsync(): Promise<void> {
    if (this.documentTypes.length > 0) {
      return;
    }

    try {
      const response = await this.vehicleDocumentsService.getDocumentTypes(this.cancellation, true);
      this.documentTypes = response.data ?? [];
    } catch {
      this.documentTypes = [];
    }
  }

  getChecklistSections(): ChecklistTableSectionConfig[] {
    return [
      this.getChecklistSection(ChecklistTypeIdEnum.Document),
      this.getChecklistSection(ChecklistTypeIdEnum.Inspection)
    ];
  }

  private getChecklistColumns(): ChecklistTableColumn[] {
    return [
      { value: 'name', width: '4fr' },
      {
        header: this.transloco.translate('compliance.library.checklistRequirements.columns.action'),
        value: 'action',
        width: '0.8fr',
        type: 'action' as const,
        align: 'right' as const,
        actionName: 'restore',
        actionIcon: 'ph ph-arrow-counter-clockwise',
        actionLabel: this.transloco.translate('compliance.library.checklistRequirements.restoreRequirement')
      }
    ];
  }

  async onRequirementActionAsync(event: { row: ChecklistRequirement; actionName: string }): Promise<void> {
    if (event.actionName === 'restore') {
      await this.onRestoreRequirementAsync(event.row);
    }
  }

  private mapChecklistGroups(checklists: ComplianceChecklist[]): ChecklistTableGroup<ChecklistRequirement>[] {
    return checklists.map(checklist => ({
      id: checklist.id,
      name: checklist.name,
      type: checklist.checklistTypeId,
      rows: checklist.requirements
    }));
  }

  private getChecklistSection(typeId: ChecklistTypeIdEnum): ChecklistTableSectionConfig {
    const checklistType = CHECKLIST_TYPES.find(type => type.id === typeId)!;

    return {
      type: checklistType.id,
      typeAliases: [checklistType.name],
      title: this.transloco.translate('compliance.library.checklistRequirements.sectionTitle', {
        type: this.transloco.translate(CHECKLIST_TYPE_NAME_KEYS[checklistType.id])
      }),
      columns: this.getChecklistColumns(),
      showColumnHeaders: false
    };
  }
}
